import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Layers } from "lucide-react";
import BadgeIcon from "@/components/BadgeIcon";
import Dialog from "@/components/modals/Dialog";
import { useAuth } from "@/contexts/AuthContext";
import { useNotification } from "@/contexts/NotificationContext";
import LoadingOverlay from "@/components/overlays/LoadingOverlay";
import { membershipLearnView, type SessionBadge } from "@/utils/sessionBadges";
import "./ContextSwitcher.css";

export function LearnScopeMeta({
  badges,
  scope,
  inline = false,
}: {
  badges: SessionBadge[];
  scope: SessionBadge | null;
  /** Role and organization on one line. The logo trigger stacks them instead. */
  inline?: boolean;
}) {
  const roles = badges.length ? (
    badges.map((badge) => <MetaChip key={badge.key} badge={badge} />)
  ) : (
    <span className="al-shell-chip">No role</span>
  );
  const school = scope ? <MetaChip badge={scope} /> : null;

  if (inline) {
    return (
      <span className="al-shell-brand-meta al-shell-brand-meta--inline" aria-label="Role and school scope">
        <span className="al-shell-brand-meta-line">
          {roles}
          {school}
        </span>
      </span>
    );
  }

  return (
    <span className="al-shell-brand-meta" aria-label="Role and school scope">
      <span className="al-shell-brand-meta-line">{roles}</span>
      {school ? <span className="al-shell-brand-meta-line">{school}</span> : null}
    </span>
  );
}

function MetaChip({ badge }: { badge: SessionBadge }) {
  return (
    <span className="al-shell-chip" title={badge.sub || badge.label}>
      <BadgeIcon name={badge.icon} />
      <span>{badge.label}</span>
    </span>
  );
}

function contextTitle(view: ReturnType<typeof membershipLearnView>) {
  return [...view.badges.map((badge) => badge.label), view.scope?.label, view.uis.line]
    .filter(Boolean)
    .join(" · ");
}

export default function ContextSwitcher({
  variant = "shell",
  collapsed = false,
}: {
  variant?: "shell" | "profile" | "dock" | "brand";
  collapsed?: boolean;
}) {
  const { session, switchableMemberships, switchMembership, currentMembership } = useAuth();
  const { notice } = useNotification();
  const [switching, setSwitching] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const membership = currentMembership || session?.currentMembership || null;
  const options = switchableMemberships?.length
    ? switchableMemberships
    : membership
      ? [membership]
      : [];
  const canSwitch = options.length > 1;
  const orderedOptions = [
    ...options.filter((ugp) => String(ugp.id) === String(membership?.id)),
    ...options.filter((ugp) => String(ugp.id) !== String(membership?.id)),
  ];
  const current = useMemo(
    () =>
      membershipLearnView(membership, {
        roles: session?.acuityLearn?.roles,
        schoolScope: session?.acuityLearn?.schoolScope,
        resolvedSchool: session?.acuityLearn?.resolvedSchool,
      }),
    [membership, session?.acuityLearn],
  );

  const handleSwitch = async (ugpId: string | number) => {
    if (!ugpId || String(ugpId) === String(membership?.id)) {
      setPickerOpen(false);
      return;
    }
    setSwitching(true);
    setPickerOpen(false);
    try {
      const next = await switchMembership(ugpId);
      window.location.assign(next.acuityLearn?.defaultHomePath || "/");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Try another membership.";
      notice({
        type: "error",
        title: "Could not switch context",
        subtext: message,
        duration: 6000,
      });
      setSwitching(false);
    }
  };

  const openPicker = () => {
    if (!canSwitch || switching) return;
    setPickerOpen(true);
  };

  if (!membership && !canSwitch) return null;

  const switchingOverlay = switching
    ? createPortal(<LoadingOverlay isVisible message="Switching…" />, document.body)
    : null;

  const title = contextTitle(current);

  const dialog = (
    <Dialog
      open={pickerOpen && canSwitch}
      title="Switch context"
      subtitle="Switch to a different role or organization. Your home updates to match."
      onClose={() => {
        if (!switching) setPickerOpen(false);
      }}
      width="32rem"
    >
      <ul className="al-context-cards" role="listbox" aria-label="Switch current context">
        {orderedOptions.map((ugp) => {
          const option = membershipLearnView(ugp);
          const isCurrent = String(ugp.id) === String(membership?.id);
          return (
            <li key={String(ugp.id)} role="presentation">
              <button
                type="button"
                className={`al-context-card${isCurrent ? " al-context-card--current" : ""}`}
                role="option"
                aria-selected={isCurrent}
                disabled={switching}
                title={contextTitle(option)}
                onClick={() => void handleSwitch(ugp.id)}
              >
                <span className="al-context-card-main">
                  <LearnScopeMeta badges={option.badges} scope={option.scope} />
                  <span className="al-context-card-uis">{option.uis.line}</span>
                </span>
                {isCurrent ? <span className="al-context-card-tag">Current</span> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </Dialog>
  );

  const copy = (
    <span className="al-context-copy">
      <LearnScopeMeta badges={current.badges} scope={current.scope} inline />
      <span className="al-context-uis" title={current.uis.line}>
        {switching ? "Switching…" : current.uis.line}
      </span>
    </span>
  );

  if (variant === "brand") {
    return (
      <>
        <button
          type="button"
          className={`al-brand-context${pickerOpen ? " al-brand-context--open" : ""}`}
          disabled={switching}
          aria-haspopup={canSwitch ? "dialog" : undefined}
          aria-expanded={canSwitch ? pickerOpen : undefined}
          title={canSwitch ? "Switch context" : title}
          onClick={openPicker}
        >
          <LearnScopeMeta badges={current.badges} scope={current.scope} />
        </button>
        {dialog}
        {switchingOverlay}
      </>
    );
  }

  if (variant === "shell") {
    return (
      <div className={`al-shell-context${collapsed ? " al-shell-context--collapsed" : ""}`}>
        <p className="al-shell-context-label">Current context</p>
        <button
          type="button"
          className={`al-shell-context-btn${pickerOpen ? " al-shell-context-btn--open" : ""}`}
          disabled={switching}
          aria-haspopup={canSwitch ? "dialog" : undefined}
          aria-expanded={canSwitch ? pickerOpen : undefined}
          title={title}
          onClick={openPicker}
        >
          <Layers className="al-shell-context-icon" aria-hidden="true" strokeWidth={2} />
          {copy}
        </button>
        {dialog}
        {switchingOverlay}
      </div>
    );
  }

  if (variant === "dock") {
    return (
      <div className="al-dock-context">
        <button
          type="button"
          className={`al-dock-context-btn${pickerOpen ? " al-dock-context-btn--open" : ""}`}
          disabled={switching}
          aria-haspopup={canSwitch ? "dialog" : undefined}
          aria-expanded={canSwitch ? pickerOpen : undefined}
          title={title}
          onClick={openPicker}
        >
          <span className="al-dock-context-kicker">Current context</span>
          {copy}
        </button>
        {dialog}
        {switchingOverlay}
      </div>
    );
  }

  return (
    <div className="al-account-uis">
      <p className="al-account-uis-label">Current context</p>
      {canSwitch ? (
        <button
          type="button"
          className={`al-account-membership al-account-membership--switchable${
            pickerOpen ? " al-account-membership--open" : ""
          }`}
          disabled={switching}
          aria-haspopup="dialog"
          aria-expanded={pickerOpen}
          title={title}
          onClick={openPicker}
        >
          {copy}
        </button>
      ) : (
        <div className="al-account-membership">{copy}</div>
      )}
      {dialog}
      {switchingOverlay}
    </div>
  );
}
